import { z } from 'zod';

import { IsoDateTimeSchema, UuidSchema, BooleanQueryParamSchema } from './common.js';
import { PaginationQuerySchema, paginatedResponseSchema } from './pagination.js';
import { MasterCodeSchema } from './query-types.js';

/**
 * Department master (Masters → Departments). Organizational unit a user belongs
 * to (e.g. "sales", "operations", "finance"). A pure simple master - see
 * ADR-0016 / ADR-0020 for the standard shape this follows.
 */

export const DepartmentListItemSchema = z.object({
  id: UuidSchema,
  legacyId: z.number().int().nullable(),
  code: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  isActive: z.boolean(),
  sortOrder: z.number().int(),
  createdAt: IsoDateTimeSchema,
});
export type DepartmentListItem = z.infer<typeof DepartmentListItemSchema>;

export const DepartmentListQuerySchema = PaginationQuerySchema.extend({
  isActive: BooleanQueryParamSchema.optional(),
  includeDeleted: BooleanQueryParamSchema.optional(),
});
export type DepartmentListQuery = z.infer<typeof DepartmentListQuerySchema>;

export const DepartmentListResponseSchema = paginatedResponseSchema(DepartmentListItemSchema);
export type DepartmentListResponse = z.infer<typeof DepartmentListResponseSchema>;

export const DepartmentDetailSchema = DepartmentListItemSchema.extend({
  updatedAt: IsoDateTimeSchema,
  deletedAt: IsoDateTimeSchema.nullable(),
});
export type DepartmentDetail = z.infer<typeof DepartmentDetailSchema>;

const DepartmentCoreFieldsSchema = z.object({
  code: MasterCodeSchema,
  name: z.string().trim().min(1, 'Name is required').max(120),
  description: z.string().trim().max(500).nullable().optional(),
  legacyId: z.number().int().positive().nullable().optional(),
  isActive: z.boolean().optional(),
  sortOrder: z.number().int().min(0).optional(),
});

export const CreateDepartmentRequestSchema = DepartmentCoreFieldsSchema;
export type CreateDepartmentRequest = z.infer<typeof CreateDepartmentRequestSchema>;

export const UpdateDepartmentRequestSchema = DepartmentCoreFieldsSchema.partial();
export type UpdateDepartmentRequest = z.infer<typeof UpdateDepartmentRequestSchema>;
